import { z } from 'zod';

export type RingSizerValidatedField =
  | 'name'
  | 'email'
  | 'mobile'
  | 'otp'
  | 'pincode'
  | 'city'
  | 'state'
  | 'address'
  | 'street'
  | 'locality';

export type RingSizerFieldErrors = Partial<Record<RingSizerValidatedField, string>>;

const alphaWithSpaces = (fieldLabel: string) =>
  z
    .string()
    .trim()
    .min(1, `The ${fieldLabel} field is required.`)
    .regex(/^[A-Za-z\s]+$/, `The ${fieldLabel} may only contain letters.`);

const sizerMobileSchema = z
  .string()
  .trim()
  .min(1, 'The mobile field is required.')
  .transform((value) => value.replace(/\s/g, ''))
  .pipe(
    z
      .string()
      .regex(/^\d{10}$/, 'Mobile should be 10 digits and no characters')
      .regex(/^[1-9]\d{9}$/, 'Mobile number should not start with zero')
  );

const sizerEmailSchema = z
  .string()
  .trim()
  .min(1, 'The email field is required.')
  .email('The email field must be a valid email address.');

const sizerPincodeSchema = z
  .string()
  .trim()
  .min(1, 'The pincode field is required.')
  .regex(/^[0-9]+$/, 'The pincode should only contain numbers.')
  .refine((value) => value.length >= 6, 'The pincode field must be at least 6 characters.');

const sizerOtpSchema = z
  .string()
  .trim()
  .min(1, 'The otp field is required.')
  .regex(/^[0-9]+$/, 'The otp should only contain numbers.')
  .refine((value) => value.length === 4, 'The otp field must be at least 4 characters.');

export const ringSizerSendOtpSchema = z.object({
  email: sizerEmailSchema,
  mobile: sizerMobileSchema,
});

export const ringSizerVerifyOtpSchema = z.object({
  otp: sizerOtpSchema,
});

export const ringSizerSubmitSchema = z.object({
  name: alphaWithSpaces('name'),
  email: sizerEmailSchema,
  mobile: sizerMobileSchema,
  pincode: sizerPincodeSchema,
  city: alphaWithSpaces('city'),
  state: z.string().trim().min(1, 'The state field is required.'),
  address: z.string().trim().min(1, 'The address field is required.'),
  street: z.string().trim().min(4, 'The street field must be at least 4 characters.'),
  locality: z.string().trim().min(4, 'The locality field must be at least 4 characters.'),
});

function flattenFieldErrors(error: z.ZodError): RingSizerFieldErrors {
  const formatted: RingSizerFieldErrors = {};
  const fieldErrors = error.flatten().fieldErrors as Partial<
    Record<RingSizerValidatedField, string[] | undefined>
  >;

  for (const [key, messages] of Object.entries(fieldErrors) as Array<
    [RingSizerValidatedField, string[] | undefined]
  >) {
    if (messages?.[0]) {
      formatted[key] = messages[0];
    }
  }

  return formatted;
}

export function validateRingSizerSendOtp(data: {
  email: string;
  mobile: string;
}): RingSizerFieldErrors {
  const result = ringSizerSendOtpSchema.safeParse(data);
  return result.success ? {} : flattenFieldErrors(result.error);
}

export function validateRingSizerEmail(email: string): string | undefined {
  const result = sizerEmailSchema.safeParse(email);
  if (result.success) return undefined;
  return result.error.issues[0]?.message;
}

export function validateRingSizerMobile(mobile: string): string | undefined {
  const result = sizerMobileSchema.safeParse(mobile);
  if (result.success) return undefined;
  return result.error.issues[0]?.message;
}

export function validateRingSizerVerifyOtp(data: { otp: string }): RingSizerFieldErrors {
  const result = ringSizerVerifyOtpSchema.safeParse(data);
  return result.success ? {} : flattenFieldErrors(result.error);
}

export function validateRingSizerSubmit(data: {
  name: string;
  email: string;
  mobile: string;
  pincode: string;
  city: string;
  state: string;
  address: string;
  street: string;
  locality: string;
}): RingSizerFieldErrors {
  const result = ringSizerSubmitSchema.safeParse(data);
  return result.success ? {} : flattenFieldErrors(result.error);
}

export function canSendRingSizerOtp(data: { email: string; mobile: string }): boolean {
  return ringSizerSendOtpSchema.safeParse(data).success;
}
